/**
 * Distribution API Client
 *
 * API functions for the VOC Rewards Phase 2 distribution system.
 * Covers: config, compound/swap/revenue entries, exclusions, overrides,
 * royalties, distribution periods, and holder endpoints.
 */

import { apiClient } from './client';
import { getCsrfToken } from './settings';

// ---- Types ----

export interface RewardsConfig {
  distribution_mode: 'all_holders' | 'claim_required';
  auto_advance: boolean;
  period_duration_days: number | null;
  reward_address_type: string;
  reward_currency_label: string;
  pool_source_default: 'compound_entries' | 'manual_pool' | 'manual_rate';
  min_points_threshold: number;
  eligible_collection_ids?: string[];
}

export interface CompoundEntry {
  id: string;
  project_id: string;
  entry_date: string;
  amount: number;
  notes: string | null;
  period_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface SwapEntry {
  id: string;
  project_id: string;
  entry_date: string;
  from_amount: number;
  from_currency: string;
  to_amount: number;
  to_currency: string;
  rate: number | null;
  notes: string | null;
  created_at: string;
}

export interface RevenueEntry {
  id: string;
  project_id: string;
  entry_date: string;
  amount: number;
  currency: string;
  label: string;
  notes: string | null;
  created_at: string;
}

export interface WalletExclusion {
  id: string;
  project_id: string;
  xrpl_address: string;
  label: string | null;
  exclude_from: string | null;
  exclude_until: string | null;
  created_at: string;
}

export interface RewardAddressOverride {
  id: string;
  project_id: string;
  xrpl_address: string;
  reward_address: string;
  label: string | null;
  created_at: string;
}

export interface DistributionPeriod {
  id: string;
  project_id: string;
  period_number: number;
  status: 'active' | 'pending_distribution' | 'distributed';
  pool_source: string;
  distribution_mode: string;
  total_pool: number | null;
  rate_per_point: number | null;
  manual_rate_per_point: number | null;
  started_at: string;
  ended_at: string | null;
  distributed_at: string | null;
  total_eligible_points: number | null;
  total_eligible_wallets: number | null;
  total_excluded_wallets: number | null;
  total_unregistered_wallets: number | null;
  compound_sum: number | null;
  coreum_usd_price: number | null;
  xrp_usd_price: number | null;
  royalty_total_drops: number | null;
  royalty_sale_count: number | null;
  eligible_collection_ids: string[];
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface DistributionRecipient {
  id: string;
  period_id: string;
  xrpl_address: string;
  reward_address: string | null;
  points: number;
  payout_amount: number;
  rank: number | null;
  is_excluded: boolean;
  is_unregistered: boolean;
  points_breakdown: Record<string, { name: string; points: number; nft_count: number }> | null;
  created_at: string;
}

export interface RoyaltySummary {
  total_royalty_drops: number;
  total_royalty_xrp: number;
  sale_count: number;
  period_start: string | null;
  period_end: string | null;
}

export interface RoyaltySale {
  id: string;
  transaction_hash: string;
  transaction_type: string;
  price_in_drops: number;
  royalty_amount_drops: number;
  royalty_is_estimated: boolean;
  seller_address: string | null;
  buyer_address: string | null;
  nft_token_id: string;
  nft_name: string | null;
  collection_name: string | null;
  transaction_date: string;
}

export interface ActivePeriodLiveStats {
  period: DistributionPeriod;
  live_stats: {
    unassigned_compound_sum: number;
    unassigned_compound_count: number;
    total_eligible_wallets: number;
    total_eligible_points: number;
    excluded_wallet_count: number;
    claim_count: number;
    estimated_rate_per_point: number | null;
  };
}

export interface HolderRewards {
  activePeriod: DistributionPeriod | null;
  myRewards: Array<DistributionRecipient & {
    period_number: number;
    period_status: string;
    period_started_at: string | null;
    period_ended_at: string | null;
    period_distributed_at: string | null;
  }>;
  pastRewards: Array<DistributionRecipient & {
    period_number: number;
    period_status: string;
    period_started_at: string | null;
    period_ended_at: string | null;
    period_distributed_at: string | null;
  }>;
}

// ---- Helpers ----

async function csrfHeaders(): Promise<Record<string, string>> {
  const csrfToken = await getCsrfToken();
  return {
    'Content-Type': 'application/json',
    'X-CSRF-Token': csrfToken,
  };
}

// ---- Config ----

export async function getRewardsConfig(projectSlug: string): Promise<{ config: RewardsConfig }> {
  return apiClient.get<{ config: RewardsConfig }>(`/projects/${projectSlug}/rewards/config`);
}

export async function updateRewardsConfig(
  projectSlug: string,
  config: Partial<RewardsConfig>
): Promise<{ config: RewardsConfig }> {
  const headers = await csrfHeaders();
  return apiClient.request<{ config: RewardsConfig }>(`/projects/${projectSlug}/rewards/config`, {
    method: 'PUT',
    headers,
    body: JSON.stringify(config),
  });
}

// ---- Compound Entries ----

export async function getCompoundEntries(
  projectSlug: string,
  options?: { limit?: number; offset?: number }
): Promise<{ entries: CompoundEntry[]; total: number; running_total: number }> {
  const params = new URLSearchParams();
  if (options?.limit) params.set('limit', String(options.limit));
  if (options?.offset) params.set('offset', String(options.offset));
  const qs = params.toString();
  return apiClient.get(`/projects/${projectSlug}/rewards/compounds${qs ? `?${qs}` : ''}`);
}

export async function createCompoundEntry(
  projectSlug: string,
  data: { entry_date: string; amount: number; notes?: string }
): Promise<{ entry: CompoundEntry }> {
  const headers = await csrfHeaders();
  return apiClient.request(`/projects/${projectSlug}/rewards/compounds`, {
    method: 'POST',
    headers,
    body: JSON.stringify(data),
  });
}

export async function updateCompoundEntry(
  projectSlug: string,
  entryId: string,
  data: { entry_date?: string; amount?: number; notes?: string | null }
): Promise<{ entry: CompoundEntry }> {
  const headers = await csrfHeaders();
  return apiClient.request(`/projects/${projectSlug}/rewards/compounds/${entryId}`, {
    method: 'PUT',
    headers,
    body: JSON.stringify(data),
  });
}

export async function deleteCompoundEntry(
  projectSlug: string,
  entryId: string
): Promise<{ success: boolean }> {
  const headers = await csrfHeaders();
  return apiClient.request(`/projects/${projectSlug}/rewards/compounds/${entryId}`, {
    method: 'DELETE',
    headers,
  });
}

// ---- Swap Entries ----

export async function getSwapEntries(
  projectSlug: string,
  options?: { limit?: number; offset?: number }
): Promise<{ entries: SwapEntry[]; total: number }> {
  const params = new URLSearchParams();
  if (options?.limit) params.set('limit', String(options.limit));
  if (options?.offset) params.set('offset', String(options.offset));
  const qs = params.toString();
  return apiClient.get(`/projects/${projectSlug}/rewards/swaps${qs ? `?${qs}` : ''}`);
}

export async function createSwapEntry(
  projectSlug: string,
  data: {
    entry_date: string;
    from_amount: number;
    from_currency: string;
    to_amount: number;
    to_currency: string;
    rate?: number;
    notes?: string;
  }
): Promise<{ entry: SwapEntry }> {
  const headers = await csrfHeaders();
  return apiClient.request(`/projects/${projectSlug}/rewards/swaps`, {
    method: 'POST',
    headers,
    body: JSON.stringify(data),
  });
}

export async function updateSwapEntry(
  projectSlug: string,
  entryId: string,
  data: Partial<{
    entry_date: string;
    from_amount: number;
    from_currency: string;
    to_amount: number;
    to_currency: string;
    rate: number | null;
    notes: string | null;
  }>
): Promise<{ entry: SwapEntry }> {
  const headers = await csrfHeaders();
  return apiClient.request(`/projects/${projectSlug}/rewards/swaps/${entryId}`, {
    method: 'PUT',
    headers,
    body: JSON.stringify(data),
  });
}

export async function deleteSwapEntry(
  projectSlug: string,
  entryId: string
): Promise<{ success: boolean }> {
  const headers = await csrfHeaders();
  return apiClient.request(`/projects/${projectSlug}/rewards/swaps/${entryId}`, {
    method: 'DELETE',
    headers,
  });
}

// ---- Revenue Entries ----

export async function getRevenueEntries(
  projectSlug: string,
  options?: { limit?: number; offset?: number }
): Promise<{ entries: RevenueEntry[]; total: number }> {
  const params = new URLSearchParams();
  if (options?.limit) params.set('limit', String(options.limit));
  if (options?.offset) params.set('offset', String(options.offset));
  const qs = params.toString();
  return apiClient.get(`/projects/${projectSlug}/rewards/revenue${qs ? `?${qs}` : ''}`);
}

export async function createRevenueEntry(
  projectSlug: string,
  data: {
    entry_date: string;
    amount: number;
    currency: string;
    label: string;
    notes?: string;
  }
): Promise<{ entry: RevenueEntry }> {
  const headers = await csrfHeaders();
  return apiClient.request(`/projects/${projectSlug}/rewards/revenue`, {
    method: 'POST',
    headers,
    body: JSON.stringify(data),
  });
}

export async function updateRevenueEntry(
  projectSlug: string,
  entryId: string,
  data: Partial<{
    entry_date: string;
    amount: number;
    currency: string;
    label: string;
    notes: string | null;
  }>
): Promise<{ entry: RevenueEntry }> {
  const headers = await csrfHeaders();
  return apiClient.request(`/projects/${projectSlug}/rewards/revenue/${entryId}`, {
    method: 'PUT',
    headers,
    body: JSON.stringify(data),
  });
}

export async function deleteRevenueEntry(
  projectSlug: string,
  entryId: string
): Promise<{ success: boolean }> {
  const headers = await csrfHeaders();
  return apiClient.request(`/projects/${projectSlug}/rewards/revenue/${entryId}`, {
    method: 'DELETE',
    headers,
  });
}

// ---- Wallet Exclusions ----

export async function getWalletExclusions(
  projectSlug: string,
  options?: { limit?: number; offset?: number }
): Promise<{ exclusions: WalletExclusion[]; total: number }> {
  const params = new URLSearchParams();
  if (options?.limit) params.set('limit', String(options.limit));
  if (options?.offset) params.set('offset', String(options.offset));
  const qs = params.toString();
  return apiClient.get(`/projects/${projectSlug}/rewards/exclusions${qs ? `?${qs}` : ''}`);
}

export async function createWalletExclusion(
  projectSlug: string,
  data: {
    xrpl_address: string;
    label?: string;
    exclude_from?: string;
    exclude_until?: string;
  }
): Promise<{ exclusion: WalletExclusion }> {
  const headers = await csrfHeaders();
  return apiClient.request(`/projects/${projectSlug}/rewards/exclusions`, {
    method: 'POST',
    headers,
    body: JSON.stringify(data),
  });
}

export async function updateWalletExclusion(
  projectSlug: string,
  exclusionId: string,
  data: Partial<{
    label: string | null;
    exclude_from: string | null;
    exclude_until: string | null;
  }>
): Promise<{ exclusion: WalletExclusion }> {
  const headers = await csrfHeaders();
  return apiClient.request(`/projects/${projectSlug}/rewards/exclusions/${exclusionId}`, {
    method: 'PUT',
    headers,
    body: JSON.stringify(data),
  });
}

export async function deleteWalletExclusion(
  projectSlug: string,
  exclusionId: string
): Promise<{ success: boolean }> {
  const headers = await csrfHeaders();
  return apiClient.request(`/projects/${projectSlug}/rewards/exclusions/${exclusionId}`, {
    method: 'DELETE',
    headers,
  });
}

// ---- Address Overrides ----

export async function getAddressOverrides(
  projectSlug: string,
  options?: { limit?: number; offset?: number }
): Promise<{ overrides: RewardAddressOverride[]; total: number }> {
  const params = new URLSearchParams();
  if (options?.limit) params.set('limit', String(options.limit));
  if (options?.offset) params.set('offset', String(options.offset));
  const qs = params.toString();
  return apiClient.get(`/projects/${projectSlug}/rewards/address-overrides${qs ? `?${qs}` : ''}`);
}

export async function createAddressOverride(
  projectSlug: string,
  data: { xrpl_address: string; reward_address: string; label?: string }
): Promise<{ override: RewardAddressOverride }> {
  const headers = await csrfHeaders();
  return apiClient.request(`/projects/${projectSlug}/rewards/address-overrides`, {
    method: 'POST',
    headers,
    body: JSON.stringify(data),
  });
}

export async function updateAddressOverride(
  projectSlug: string,
  overrideId: string,
  data: Partial<{ reward_address: string; label: string | null }>
): Promise<{ override: RewardAddressOverride }> {
  const headers = await csrfHeaders();
  return apiClient.request(`/projects/${projectSlug}/rewards/address-overrides/${overrideId}`, {
    method: 'PUT',
    headers,
    body: JSON.stringify(data),
  });
}

export async function deleteAddressOverride(
  projectSlug: string,
  overrideId: string
): Promise<{ success: boolean }> {
  const headers = await csrfHeaders();
  return apiClient.request(`/projects/${projectSlug}/rewards/address-overrides/${overrideId}`, {
    method: 'DELETE',
    headers,
  });
}

// ---- Royalties ----

export async function getRoyaltySummary(
  projectSlug: string,
  options?: { start_date?: string; end_date?: string }
): Promise<{ summary: RoyaltySummary }> {
  const params = new URLSearchParams();
  if (options?.start_date) params.set('start_date', options.start_date);
  if (options?.end_date) params.set('end_date', options.end_date);
  const qs = params.toString();
  return apiClient.get(`/projects/${projectSlug}/rewards/royalties${qs ? `?${qs}` : ''}`);
}

export async function getRoyaltySales(
  projectSlug: string,
  options?: { limit?: number; offset?: number; start_date?: string; end_date?: string }
): Promise<{ sales: RoyaltySale[]; total: number }> {
  const params = new URLSearchParams();
  if (options?.limit) params.set('limit', String(options.limit));
  if (options?.offset) params.set('offset', String(options.offset));
  if (options?.start_date) params.set('start_date', options.start_date);
  if (options?.end_date) params.set('end_date', options.end_date);
  const qs = params.toString();
  return apiClient.get(`/projects/${projectSlug}/rewards/royalties/sales${qs ? `?${qs}` : ''}`);
}

// ---- COREUM Price ----

export async function getCoreumPrice(): Promise<{ price_usd: number; currency: string }> {
  return apiClient.get('/prices/coreum');
}

// ---- Distribution Periods (Owner) ----

export async function startDistributionPeriod(
  projectSlug: string,
  data: {
    eligible_collection_ids: string[];
    pool_source?: string;
    distribution_mode?: string;
    notes?: string;
  }
): Promise<{ period: DistributionPeriod }> {
  const headers = await csrfHeaders();
  return apiClient.request(`/projects/${projectSlug}/rewards/dist-periods`, {
    method: 'POST',
    headers,
    body: JSON.stringify(data),
  });
}

export async function getDistributionPeriods(
  projectSlug: string
): Promise<{ periods: DistributionPeriod[] }> {
  return apiClient.get(`/projects/${projectSlug}/rewards/dist-periods`);
}

export async function getActiveDistributionPeriod(
  projectSlug: string
): Promise<ActivePeriodLiveStats | { period: null }> {
  return apiClient.get(`/projects/${projectSlug}/rewards/dist-periods/active`);
}

export async function endDistributionPeriod(
  projectSlug: string,
  periodId: string,
  data?: { total_pool?: number; manual_rate_per_point?: number }
): Promise<{ period: DistributionPeriod }> {
  const headers = await csrfHeaders();
  return apiClient.request(`/projects/${projectSlug}/rewards/dist-periods/${periodId}/end`, {
    method: 'POST',
    headers,
    body: JSON.stringify(data || {}),
  });
}

export async function distributeDistributionPeriod(
  projectSlug: string,
  periodId: string
): Promise<{ period: DistributionPeriod; newPeriod?: DistributionPeriod }> {
  const headers = await csrfHeaders();
  return apiClient.request(`/projects/${projectSlug}/rewards/dist-periods/${periodId}/distribute`, {
    method: 'POST',
    headers,
    body: JSON.stringify({}),
  });
}

export async function getDistributionRecipients(
  projectSlug: string,
  periodId: string,
  options?: { limit?: number; offset?: number; includeExcluded?: boolean }
): Promise<{ recipients: DistributionRecipient[]; total: number }> {
  const params = new URLSearchParams();
  if (options?.limit) params.set('limit', String(options.limit));
  if (options?.offset) params.set('offset', String(options.offset));
  if (options?.includeExcluded) params.set('includeExcluded', 'true');
  const qs = params.toString();
  return apiClient.get(
    `/projects/${projectSlug}/rewards/dist-periods/${periodId}/recipients${qs ? `?${qs}` : ''}`
  );
}

export async function exportDistributionCSV(projectSlug: string, periodId: string): Promise<void> {
  const token = localStorage.getItem('xrpl_marketplace_token');
  const baseUrl = (apiClient as any)['baseUrl'] || '/api/v1';
  const url = `${baseUrl}/projects/${projectSlug}/rewards/dist-periods/${periodId}/export`;

  const response = await fetch(url, {
    headers: {
      'Authorization': token ? `Bearer ${token}` : '',
    },
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to download CSV');
  }

  const blob = await response.blob();
  const downloadUrl = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = downloadUrl;
  a.download = `distribution-${projectSlug}-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(downloadUrl);
}

// ---- Distribution (Holder) ----

export async function submitDistributionClaim(
  projectSlug: string
): Promise<{ success: boolean; claimed_count: number }> {
  const headers = await csrfHeaders();
  return apiClient.request(`/projects/${projectSlug}/rewards/dist-claim`, {
    method: 'POST',
    headers,
    body: JSON.stringify({}),
  });
}

export async function getMyDistributionRewards(
  projectSlug: string
): Promise<HolderRewards> {
  return apiClient.get(`/projects/${projectSlug}/rewards/my-rewards`);
}

export async function getPublicActivePeriodSummary(
  projectSlug: string
): Promise<{ period: DistributionPeriod | null }> {
  return apiClient.get(`/projects/${projectSlug}/rewards/dist-periods/active/summary`);
}

export async function getPublicDistributionHistory(
  projectSlug: string
): Promise<{ periods: DistributionPeriod[] }> {
  return apiClient.get(`/projects/${projectSlug}/rewards/dist-periods/public`);
}
